import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import ReactSpeedometer from "react-d3-speedometer";

// Import ZIP to County data - you'll bundle this with your app
// For now, we'll use a placeholder that will be replaced
const ZIP_TO_COUNTY_DATA = {}; // This will be loaded from zip_to_county_data.json

// YBGR Brand Colors
const COLORS = {
  blue: '#274F66',      // Primary - headers, main elements
  gold: '#F4AA3F',      // Secondary - buttons, links, accents
  green: '#4F6E32',     // Tertiary - reserved for special uses
  lightBlue: '#E8F0F5', // Light blue for backgrounds
  darkBlue: '#1a3a4a'   // Darker blue for contrast
};

// TGH and PRTF facility lists
const TGH_FACILITIES = [
  'Rivers Edge Youth Home', 'STAR Youth Home', 'Lakeview Youth Home',
  'Opportunity Youth Home', 'New Beginnings Youth Home', 
  'Lewis and Clark Youth Home', 'North Skyline Youth Home'
];

const PRTF_FACILITIES = [
  'Kramlich', "Dorothy's", 'Jessie Grant', 'Shumaker', 
  'Fortin East', 'Fortin West', 'Leuthold', 'McVay', 'King'
];

// Age group helper
const getAgeGroup = (age) => {
  if (age >= 0 && age <= 5) return '0-5';
  if (age >= 6 && age <= 12) return '6-12';
  if (age >= 13 && age <= 17) return '13-17';
  return '18+';
};

// Main Dashboard Component
const BLOB_BASE = 'https://ybgrdashboardstorage.blob.core.windows.net/dashboard-data';

// Archive mode shows a CLOSED fiscal or calendar year instead of today's snapshot:
// unique kids served across the whole period rather than who is active right now.
// Entered via /archive or ?period=fy2026 so a specific year is linkable.
const readArchiveRequest = () => {
  const params = new URLSearchParams(window.location.search);
  const period = params.get('period');
  return {
    active: Boolean(period) || /\/archive\/?$/.test(window.location.pathname),
    period,
  };
};

// YBGR acquired group homes on 2024-10-05 with another agency; before that it had not run
// one in years (Lewistown was the last). TGH records also took time to land in the EHR,
// and only became reliable at the 2025-01-16 "must be on the new EHR" cutover. A reader
// seeing TGH = 0 for FY2019, or a suspiciously low TGH number for FY2025, would otherwise
// have no way to know which of those is real.
const TGH_PROGRAM_START = '2024-10-05';
const TGH_EHR_RELIABLE_FROM = '2025-01-16';

const archivePeriodNotes = (metadata) => {
  if (!metadata || !metadata.windowStart || !metadata.windowEnd) return [];
  const notes = [];
  // ISO dates compare correctly as strings.
  if (metadata.windowEnd < TGH_PROGRAM_START) {
    notes.push('YBGR did not operate group homes during this period, so TGH counts are zero by design.');
  } else if (metadata.windowStart < TGH_EHR_RELIABLE_FROM) {
    notes.push('Group homes were acquired 10/5/2024 and TGH records were not fully in the EHR until 1/16/2025, so TGH counts for this period understate actual service.');
  }
  return notes;
};

// Module scope on purpose: it touches only window.location, so keeping it out of the
// component gives it a stable identity and effects can call it without listing it as
// a dependency (which would otherwise re-run them on every render).
const syncPeriodToUrl = (periodId) => {
  const url = new URL(window.location.href);
  if (periodId) {
    url.searchParams.set('period', periodId);
  } else {
    url.searchParams.delete('period');
  }
  window.history.replaceState({}, '', url);
};

const YBGRDashboard = () => {
  const initialArchive = readArchiveRequest();

  const [activeTab, setActiveTab] = useState('geographic');
  const [menuOpen, setMenuOpen] = useState(false);
  const [processedData, setProcessedData] = useState(null);
  const [dataDate, setDataDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [archiveMode, setArchiveMode] = useState(initialArchive.active);
  const [selectedPeriod, setSelectedPeriod] = useState(initialArchive.period);
  const [archiveIndex, setArchiveIndex] = useState(null);
  const [periodKind, setPeriodKind] = useState(
    (initialArchive.period || '').startsWith('cy') ? 'calendar' : 'fiscal'
  );

  // Beds is a point-in-time census, so it has no meaning across a 12-month window.
  // The archive puts a county table in that slot instead -- the map shows distribution,
  // this gives the actual numbers people put in a report.
  const showBedsTab = !archiveMode;
  const showCountyListTab = archiveMode;
  const showTrendsTab = archiveMode;

  const choosePeriod = (periodId) => {
    setSelectedPeriod(periodId);
    syncPeriodToUrl(periodId);
  };

  const choosePeriodKind = (kind) => {
    setPeriodKind(kind);
    const match = (archiveIndex?.periods || []).find((p) => p.kind === kind);
    if (match) choosePeriod(match.id);
  };

  const enterArchiveMode = () => {
    setArchiveMode(true);
    if (activeTab === 'beds') setActiveTab('geographic');
    setMenuOpen(false);
  };

  const exitArchiveMode = () => {
    setArchiveMode(false);
    setSelectedPeriod(null);
    setDataDate(null);
    if (activeTab === 'counties' || activeTab === 'trends') setActiveTab('geographic');
    syncPeriodToUrl(null);
    setMenuOpen(false);
  };

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data from Azure Blob on mount
  useEffect(() => {
    const loadDataFromBlob = async () => {
      try {
        setLoading(true);
        
        // Add cache-busting timestamp to force fresh data on each load
        // Clear any previous failure -- switching between live and archive retries the
        // load, and a stale error would otherwise mask data that loaded fine.
        setError(null);

        // In archive mode, wait for the index to pick a default period before fetching.
        if (archiveMode && !selectedPeriod) return;

        const timestamp = new Date().getTime();
        const dataUrl = archiveMode && selectedPeriod
          ? `${BLOB_BASE}/archive/${selectedPeriod}.json?t=${timestamp}`
          : `${BLOB_BASE}/client-data.json?t=${timestamp}`;
        const response = await fetch(
          dataUrl,
          {
            cache: 'no-store', // Prevent browser caching
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to load data: ${response.status}`);
        }
        
        const data = await response.json();
        
        setProcessedData(data);
        
        // Format the date from metadata - convert UTC to local time
        if (data.metadata && data.metadata.processedDate) {
          // The timestamp from Azure is in UTC but doesn't have a 'Z' suffix
          // Add 'Z' to explicitly mark it as UTC, or append it if it doesn't exist
          let utcDateString = data.metadata.processedDate;
          if (!utcDateString.endsWith('Z') && !utcDateString.includes('+')) {
            utcDateString += 'Z';
          }
          
          const date = new Date(utcDateString);
          
          // Format with both date and time in user's local timezone
          const formattedDate = date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric'
          });
          
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          
          setDataDate(`${formattedDate} at ${formattedTime}`);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setError(error.message);
        setLoading(false);
      }
    };
    
    loadDataFromBlob();
  }, [archiveMode, selectedPeriod]);

  // Archive index -- the list of periods that have actually been compiled.
  useEffect(() => {
    if (!archiveMode || archiveIndex) return;

    const loadArchiveIndex = async () => {
      try {
        setError(null);
        const response = await fetch(`${BLOB_BASE}/archive/index.json?t=${new Date().getTime()}`, {
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error(`Failed to load archive index: ${response.status}`);
        }
        const index = await response.json();
        setArchiveIndex(index);

        if (!selectedPeriod) {
          const periods = index.periods || [];
          const first = periods.find((p) => p.kind === periodKind) || periods[0];
          if (first) {
            setSelectedPeriod(first.id);
            syncPeriodToUrl(first.id);
          } else {
            setError('No archived periods are available yet.');
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Error loading archive index:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadArchiveIndex();
  }, [archiveMode, archiveIndex, selectedPeriod, periodKind]);

  return (
    <div style={{
      fontFamily: '"Darker Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      minHeight: '100vh',
      padding: '0',
      margin: '0',
    }}>
      {/* Header */}
      <header style={{
        background: `linear-gradient(135deg, ${COLORS.blue} 0%, ${COLORS.darkBlue} 100%)`,
        color: 'white',
        padding: isMobile ? '1.5rem 1rem' : (isTablet ? '2rem 1.5rem' : '2rem 3rem'),
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '400px',
          height: '100%',
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-end',
            justifyContent: 'flex-start',
            gap: '1rem',
            marginBottom: '0.5rem'
          }}>
            <img 
              src="/ybgr-logo-whiteinverted-300px.png" 
              alt="YBGR Logo" 
              style={{ 
                height: isMobile ? '50px' : (isTablet ? '70px' : '80px'),
                filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))'
              }} 
            />
            <h1 style={{
              margin: 0,
              fontSize: isMobile ? '1.75rem' : (isTablet ? '2.5rem' : '3rem'),
              fontWeight: '800',
              letterSpacing: '-0.02em',
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              lineHeight: 1
            }}>
              Dashboard
            </h1>
          </div>
          <p style={{
            margin: 0,
            fontSize: isMobile ? '1rem' : (isTablet ? '1rem' : '1rem'),
            opacity: 0.95,
            fontWeight: '500',
            letterSpacing: '0.02em',
            fontStyle: 'italic',
            textAlign: 'left'
          }}>
            Caring People Preparing Youth for Life
          </p>

          {/* Archive controls: switch between today's snapshot and a completed year */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.5rem',
            margin: '0.75rem 0 0 0',
          }}>
            {!archiveMode ? (
              <button
                onClick={enterArchiveMode}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '4px',
                  color: 'white',
                  padding: '0.3rem 0.65rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => (e.target.style.background = 'rgba(255,255,255,0.3)')}
                onMouseLeave={(e) => (e.target.style.background = 'rgba(255,255,255,0.2)')}
                title="View unique clients served across a completed fiscal or calendar year"
              >
                📅 Year Archive
              </button>
            ) : (
              <>
                <button
                  onClick={exitArchiveMode}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: '4px',
                    color: 'white',
                    padding: '0.3rem 0.65rem',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                  title="Return to today's live dashboard"
                >
                  ← Live Dashboard
                </button>

                <div style={{
                  display: 'flex',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  {[['fiscal', 'Fiscal Year'], ['calendar', 'Calendar Year']].map(([kind, label]) => (
                    <button
                      key={kind}
                      onClick={() => choosePeriodKind(kind)}
                      style={{
                        background: periodKind === kind ? COLORS.gold : 'rgba(255,255,255,0.1)',
                        color: periodKind === kind ? COLORS.blue : 'white',
                        border: 'none',
                        padding: '0.3rem 0.65rem',
                        fontSize: '0.8rem',
                        fontWeight: periodKind === kind ? '700' : '500',
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <select
                  value={selectedPeriod || ''}
                  onChange={(e) => choosePeriod(e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    color: COLORS.blue,
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: '4px',
                    padding: '0.3rem 0.5rem',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    maxWidth: isMobile ? '100%' : '320px',
                  }}
                  aria-label="Select archived period"
                >
                  {/* Sort here rather than trusting index.json's order -- newest first is
                      what people expect, and during a backfill the index fills in out of
                      sequence. */}
                  {(archiveIndex?.periods || [])
                    .filter((p) => p.kind === periodKind)
                    .sort((a, b) => b.year - a.year)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                </select>
              </>
            )}
          </div>

          {archiveMode && processedData?.metadata?.periodLabel && (
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.8rem',
              opacity: 0.9,
              fontStyle: 'italic',
            }}>
              {processedData.metadata.periodLabel}
              {typeof processedData?.categoryTotals?.totalUniqueClients === 'number' &&
                ` — ${processedData.categoryTotals.totalUniqueClients.toLocaleString()} unique clients served`}
            </p>
          )}

          {/* Completeness footnote: a historical number should carry its own caveats,
              since nothing about a closed year can be corrected after the fact. */}
          {archiveMode && processedData?.dataQuality?.clientsInUnknownCounty > 0 && (
            <p style={{
              margin: '0.15rem 0 0 0',
              fontSize: '0.72rem',
              opacity: 0.75,
            }}>
              {processedData.dataQuality.clientsInUnknownCounty.toLocaleString()}
              {processedData.dataQuality.clientsInUnknownCounty === 1
                ? ' client had no usable ZIP code and is grouped under "Unknown" county.'
                : ' clients had no usable ZIP code and are grouped under "Unknown" county.'}
            </p>
          )}

          {archiveMode && archivePeriodNotes(processedData?.metadata).map((note) => (
            <p key={note} style={{
              margin: '0.15rem 0 0 0',
              fontSize: '0.72rem',
              opacity: 0.75,
            }}>
              {note}
            </p>
          ))}

          {!archiveMode && dataDate && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              margin: '0.5rem 0 0 0'
            }}>
              <p style={{
                margin: 0,
                fontSize: '0.8rem',
                opacity: 0.9,
                fontStyle: 'italic',
              }}>
                Data current as of {dataDate}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '4px',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                title="Refresh data"
              >
                🔄 Refresh
              </button>
            </div>
          )}
          {loading && (
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '1rem',
              opacity: 0.9,
            }}>
              Loading data...
            </p>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div style={{ 
        padding: isMobile ? '1rem' : (isTablet ? '1.5rem' : '1rem 3rem'), 
        maxWidth: '2000px', 
        margin: '0 auto' 
      }}>
        {/* Tabs - Desktop: horizontal tabs, Mobile/Tablet: hamburger menu */}
        {(isMobile || isTablet) ? (
          <div style={{ marginBottom: '2rem' }}>
            {/* Hamburger Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                background: COLORS.blue,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '700',
                cursor: 'pointer',
                width: '100%',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>☰</span>
                <span>
                  {activeTab === 'geographic' ? '📍 Geographic View' :
                   activeTab === 'programs' ? '📊 Program View' :
                   activeTab === 'counties' ? '📋 County View' :
                   activeTab === 'trends' ? '📈 Trends' :
                   '🛏️ Bed Capacity'}
                </span>
              </div>
              <span style={{ fontSize: '1.2rem' }}>{menuOpen ? '▲' : '▼'}</span>
            </button>
            
            {/* Dropdown Menu */}
            {menuOpen && (
              <div style={{
                marginTop: '0.5rem',
                background: 'white',
                border: `2px solid ${COLORS.blue}`,
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <button
                  onClick={() => {
                    setActiveTab('geographic');
                    setMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: activeTab === 'geographic' ? COLORS.lightBlue : 'white',
                    color: activeTab === 'geographic' ? COLORS.blue : '#6c757d',
                    border: 'none',
                    borderBottom: '1px solid #dee2e6',
                    fontSize: '1rem',
                    fontWeight: activeTab === 'geographic' ? '700' : '400',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  📍 Geographic View
                </button>
                <button
                  onClick={() => {
                    setActiveTab('programs');
                    setMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: activeTab === 'programs' ? COLORS.lightBlue : 'white',
                    color: activeTab === 'programs' ? COLORS.blue : '#6c757d',
                    border: 'none',
                    borderBottom: '1px solid #dee2e6',
                    fontSize: '1rem',
                    fontWeight: activeTab === 'programs' ? '700' : '400',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  📊 Program View
                </button>
                {showTrendsTab && (
                <button
                  onClick={() => {
                    setActiveTab('trends');
                    setMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: activeTab === 'trends' ? COLORS.lightBlue : 'white',
                    color: activeTab === 'trends' ? COLORS.blue : '#6c757d',
                    border: 'none',
                    fontSize: '1rem',
                    fontWeight: activeTab === 'trends' ? '700' : '400',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  📈 Trends
                </button>
                )}
                {showCountyListTab && (
                <button
                  onClick={() => {
                    setActiveTab('counties');
                    setMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: activeTab === 'counties' ? COLORS.lightBlue : 'white',
                    color: activeTab === 'counties' ? COLORS.blue : '#6c757d',
                    border: 'none',
                    fontSize: '1rem',
                    fontWeight: activeTab === 'counties' ? '700' : '400',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  📋 County View
                </button>
                )}
                {showBedsTab && (
                <button
                  onClick={() => {
                    setActiveTab('beds');
                    setMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: activeTab === 'beds' ? COLORS.lightBlue : 'white',
                    color: activeTab === 'beds' ? COLORS.blue : '#6c757d',
                    border: 'none',
                    fontSize: '1rem',
                    fontWeight: activeTab === 'beds' ? '700' : '400',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  🛏️ Bed Capacity
                </button>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Desktop Tabs */
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '2rem',
            borderBottom: '2px solid #dee2e6',
          }}>
            <button
              onClick={() => setActiveTab('geographic')}
              style={{
                padding: '1rem 2rem',
                background: activeTab === 'geographic' ? COLORS.blue : 'transparent',
                color: activeTab === 'geographic' ? 'white' : '#6c757d',
                border: 'none',
                borderBottom: activeTab === 'geographic' ? `4px solid ${COLORS.gold}` : '4px solid transparent',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            >
              📍 Geographic View
            </button>
            <button
              onClick={() => setActiveTab('programs')}
              style={{
                padding: '1rem 2rem',
                background: activeTab === 'programs' ? COLORS.blue : 'transparent',
                color: activeTab === 'programs' ? 'white' : '#6c757d',
                border: 'none',
                borderBottom: activeTab === 'programs' ? `4px solid ${COLORS.gold}` : '4px solid transparent',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            >
              📊 Program View
            </button>
            {showTrendsTab && (
            <button
              onClick={() => setActiveTab('trends')}
              style={{
                padding: '1rem 2rem',
                background: activeTab === 'trends' ? COLORS.blue : 'transparent',
                color: activeTab === 'trends' ? 'white' : '#6c757d',
                border: 'none',
                borderBottom: activeTab === 'trends' ? `4px solid ${COLORS.gold}` : '4px solid transparent',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            >
              📈 Trends
            </button>
            )}
            {showCountyListTab && (
            <button
              onClick={() => setActiveTab('counties')}
              style={{
                padding: '1rem 2rem',
                background: activeTab === 'counties' ? COLORS.blue : 'transparent',
                color: activeTab === 'counties' ? 'white' : '#6c757d',
                border: 'none',
                borderBottom: activeTab === 'counties' ? `4px solid ${COLORS.gold}` : '4px solid transparent',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            >
              📋 County View
            </button>
            )}
            {showBedsTab && (
            <button
              onClick={() => setActiveTab('beds')}
              style={{
                padding: '1rem 2rem',
                background: activeTab === 'beds' ? COLORS.blue : 'transparent',
                color: activeTab === 'beds' ? 'white' : '#6c757d',
                border: 'none',
                borderBottom: activeTab === 'beds' ? `4px solid ${COLORS.gold}` : '4px solid transparent',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            >
              🛏️ Bed Capacity
            </button>
            )}
          </div>
        )}

        {/* Tab Content */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '6px solid #f3f3f3',
              borderTop: `6px solid ${COLORS.blue}`,
              borderRadius: '50%',
              margin: '0 auto 1.5rem',
              animation: 'spin 1s linear infinite',
            }} />
            <h2 style={{ color: COLORS.blue, marginBottom: '0.5rem' }}>Loading Dashboard Data</h2>
            <p style={{ color: '#6c757d', fontSize: '1.1rem' }}>
              Fetching latest client data from Azure...
            </p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : error ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}>
            <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Error Loading Data</h2>
            <p style={{ color: '#6c757d', fontSize: '1.1rem', marginBottom: '2rem' }}>
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '1rem 2rem',
                background: COLORS.blue,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : !processedData ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}>
            <h2 style={{ color: COLORS.blue, marginBottom: '1rem' }}>No Data Available</h2>
            <p style={{ color: '#6c757d', fontSize: '1.1rem' }}>
              Please check that data has been uploaded to Azure Blob Storage.
            </p>
          </div>
        ) : activeTab === 'geographic' ? (
          <GeographicView data={processedData.countyData} isMobile={isMobile} isTablet={isTablet} />
        ) : activeTab === 'trends' && showTrendsTab ? (
          <TrendsView periodKind={periodKind} isMobile={isMobile} isTablet={isTablet} />
        ) : activeTab === 'counties' && showCountyListTab ? (
          <CountyView data={processedData.countyData} isMobile={isMobile} isTablet={isTablet} />
        ) : activeTab === 'beds' && showBedsTab ? (
          <BedsView data={processedData.bedsData} isMobile={isMobile} isTablet={isTablet} />
        ) : (
          <ProgramView data={processedData} isMobile={isMobile} isTablet={isTablet} />
        )}
      </div>
    </div>
  );
};

// Forces Leaflet to redraw all layers after map initialization AND after GeoJSON data loads.
// Fixes intermittent thin borders: invalidateSize on mount fires before async GeoJSON fetch
// completes, so we also fire it when statesLoaded changes (i.e. when the data arrives).
const MapReadyHandler = ({ statesLoaded }) => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 100);
    return () => clearTimeout(timer);
  }, [map, statesLoaded]);
  return null;
};

// Geographic View Component
const GeographicView = ({ data, isMobile, isTablet }) => {
  const [geoJsonData, setGeoJsonData] = useState({
    MT: null,
    ID: null,
    WA: null
  });
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const mapContainerRef = React.useRef(null);
  const [selectedProgramFilter, setSelectedProgramFilter] = useState('ALL'); // 'ALL' or specific program name
  
  // Extract all unique programs from the data on mount
  useEffect(() => {
    const programs = new Set();
    Object.values(data).forEach(county => {
      Object.keys(county.programs || {}).forEach(program => {
        programs.add(program);
      });
    });
    const sortedPrograms = Array.from(programs).sort();
    setAllPrograms(sortedPrograms);
  }, [data]);
  
  // Load GeoJSON for regional counties (8 western states) + all US states
  useEffect(() => {
    // Load regional counties (detailed)
    fetch('/counties-regional.json')
      .then(res => res.json())
      .then(counties => {
        setGeoJsonData(prev => ({ ...prev, counties }));
      })
      .catch(err => console.error('Error loading counties:', err));
    
    // Load all US states (for national overview)
    fetch('/us-states.json')
      .then(res => res.json())
      .then(states => {
        setGeoJsonData(prev => ({ ...prev, states }));
      })
      .catch(err => console.error('Error loading states:', err));
  }, []);
  
  // Fullscreen toggle handler
  const toggleMapFullscreen = () => {
    if (!isMapFullscreen) {
      // Enter fullscreen
      if (mapContainerRef.current) {
        if (mapContainerRef.current.requestFullscreen) {
          mapContainerRef.current.requestFullscreen();
        } else if (mapContainerRef.current.webkitRequestFullscreen) {
          mapContainerRef.current.webkitRequestFullscreen();
        } else if (mapContainerRef.current.msRequestFullscreen) {
          mapContainerRef.current.msRequestFullscreen();
        }
        setIsMapFullscreen(true);
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsMapFullscreen(false);
    }
  };
  
  // Listen for fullscreen changes (e.g., user presses ESC)
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsMapFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Get all counties with client data by state
  const countyStatsByState = useMemo(() => {
    const stats = {};
    
    Object.entries(data).forEach(([countyName, countyData]) => {
      const state = countyData.state;
      
      // Filter client count based on selected program
      let filteredClientCount = 0;
      if (selectedProgramFilter === 'ALL') {
        filteredClientCount = countyData.clientCount;
      } else {
        filteredClientCount = countyData.programs?.[selectedProgramFilter] || 0;
      }
      
      // Create filtered county data
      const filteredData = {
        ...countyData,
        clientCount: filteredClientCount
      };
      
      // Group by state (any state)
      if (!stats[state]) {
        stats[state] = {};
      }
      stats[state][countyName] = filteredData;
    });
    return stats;
  }, [data, selectedProgramFilter]);
  
  // Get unique programs for filter dropdown
  const uniquePrograms = useMemo(() => {
    const programs = new Set();
    Object.values(countyStatsByState).forEach(stateCounties => {
      Object.values(stateCounties).forEach(countyData => {
        if (countyData.programs) {
          Object.keys(countyData.programs).forEach(program => {
            programs.add(program);
          });
        }
      });
    });
    return Array.from(programs).sort();
  }, [countyStatsByState]);
  
  // Helper function to count clients for a specific program
  const getClientCountForProgram = (programName) => {
    if (programName === 'ALL') {
      return Object.values(countyStatsByState).reduce((total, stateCounties) => {
        return total + Object.values(stateCounties).reduce((sum, county) => sum + county.clientCount, 0);
      }, 0);
    }
    
    return Object.values(countyStatsByState).reduce((total, stateCounties) => {
      return total + Object.values(stateCounties).reduce((sum, county) => {
        return sum + (county.programs?.[programName] || 0);
      }, 0);
    }, 0);
  };
  
  // Color scale using YBGR blue palette - based on absolute client count
  const getColor = (clientCount) => {
    if (clientCount === 0) return '#d3d3d3'; // Gray for no data
    
    // Fixed thresholds based on actual client counts
    if (clientCount >= 100) return COLORS.blue;      // #274F66 - Dark blue for 100+
    if (clientCount >= 50) return '#3D6B85';         // Medium-dark blue for 50-99
    if (clientCount >= 20) return '#5387A4';         // Medium blue for 20-49
    if (clientCount >= 10) return '#7BA5BF';         // Light blue for 10-19
    return '#B8D4E3';                                 // Very light blue for 1-9
  };
  
  // Style function for county polygons
  // Regional states that have county-level detail
  const regionalStates = ['ID', 'MT', 'ND', 'OR', 'SD', 'UT', 'WA', 'WY'];
  const regionalStateNames = ['Idaho', 'Montana', 'North Dakota', 'Oregon', 'South Dakota', 'Utah', 'Washington', 'Wyoming'];
  
  // Style function for counties (regional detail)
  const countyStyle = (feature, stateCode) => {
    const countyName = feature.properties.NAME;
    const countyData = countyStatsByState[stateCode]?.[countyName];
    const clientCount = countyData ? countyData.clientCount : 0;
    const hasData = clientCount > 0;
    
    return {
      fillColor: getColor(clientCount),
      weight: 1.5,
      opacity: 1,
      color: 'white',
      fillOpacity: hasData ? 0.7 : 0.3
    };
  };
  
  // Style function for states (national overview)
  const stateStyle = (feature) => {
    const stateName = feature.properties.name;
    
    // Map state name to abbreviation for data lookup
    const stateNameToAbbr = {
      'Idaho': 'ID', 'Montana': 'MT', 'North Dakota': 'ND', 'Oregon': 'OR',
      'South Dakota': 'SD', 'Utah': 'UT', 'Washington': 'WA', 'Wyoming': 'WY',
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
      'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD', 'Massachusetts': 'MA',
      'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'Tennessee': 'TN', 'Texas': 'TX', 'Vermont': 'VT', 'Virginia': 'VA',
      'West Virginia': 'WV', 'Wisconsin': 'WI'
    };
    
    const stateAbbr = stateNameToAbbr[stateName];
    
    // Count total clients in this state
    const stateData = countyStatsByState[stateAbbr] || {};
    const totalClients = Object.values(stateData).reduce((sum, county) => sum + (county.clientCount || 0), 0);
    const hasData = totalClients > 0;
    
    return {
      fillColor: hasData ? getColor(totalClients) : '#d3d3d3',
      weight: 1.5,
      opacity: 1,
      color: '#999',
      fillOpacity: hasData ? 0.5 : 0.2
    };
  };
  
  // Handle county interactions (regional detail)
  const onEachCounty = (feature, layer, stateCode) => {
    const countyName = feature.properties.NAME;
    
    layer.on({
      mouseover: (e) => {
        const layer = e.target;
        layer.setStyle({
          weight: 3,
          color: COLORS.gold,
          fillOpacity: 0.9
        });
        
        // Get fresh county data on each mouseover (accounts for filter changes)
        const countyData = countyStatsByState[stateCode]?.[countyName];
        
        // Show tooltip on hover
        if (countyData) {
          // Determine what text to show based on filter
          const clientLabel = selectedProgramFilter === 'ALL' 
            ? 'clients' 
            : `${selectedProgramFilter} clients`;
          
          layer.bindTooltip(`
            <div style="font-family: 'Darker Grotesque', sans-serif; padding: 0.5rem;">
              <strong style="font-size: 1.1rem; color: ${COLORS.blue};">${countyName} County, ${stateCode}</strong><br/>
              <span style="color: ${COLORS.gold}; font-size: 1.2rem; font-weight: 800;">${countyData.clientCount}</span> <span style="font-size: 0.95rem;">${clientLabel}</span><br/>
              <span style="font-size: 0.9rem; color: #666;">
                Population: ${countyData.population.toLocaleString()}<br/>
                Median Income: $${countyData.median_income.toLocaleString()}<br/>
                Median Age: ${countyData.median_age} years
              </span>
            </div>
          `, {
            permanent: false,
            direction: 'auto',
            className: 'county-tooltip'
          }).openTooltip();
        } else {
          layer.bindTooltip(`
            <div style="font-family: 'Darker Grotesque', sans-serif; padding: 0.5rem;">
              <strong style="font-size: 1.1rem; color: #999;">${countyName} County, ${stateCode}</strong><br/>
              <em style="color: #999;">No client data</em>
            </div>
          `, {
            permanent: false,
            direction: 'auto'
          }).openTooltip();
        }
      },
      mouseout: (e) => {
        const layer = e.target;
        layer.setStyle(countyStyle(feature, stateCode));
        layer.closeTooltip();
      },
      click: () => {
        // Click opens the side panel
        // Create composite key: countyName|stateCode to handle duplicate county names across states
        const countyKey = `${countyName}|${stateCode}`;
        
        // Find the county data by matching both name and state
        let fullCountyData = null;
        Object.entries(data).forEach(([key, value]) => {
          if (key === countyName && value.state === stateCode) {
            fullCountyData = value;
          }
        });
        
        if (fullCountyData) {
          setSelectedCounty({ 
            name: countyName, 
            state: stateCode,
            data: fullCountyData 
          });
        }
      }
    });
  };
  
  // Handle state interactions (national overview)
  const onEachState = (feature, layer) => {
    const stateName = feature.properties.name;
    
    // Map state name to abbreviation
    const stateNameToAbbr = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
      'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD', 'Massachusetts': 'MA',
      'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'Tennessee': 'TN', 'Texas': 'TX', 'Vermont': 'VT', 'Virginia': 'VA',
      'West Virginia': 'WV', 'Wisconsin': 'WI'
    };
    
    const stateAbbr = stateNameToAbbr[stateName];
    
    layer.on({
      mouseover: (e) => {
        const layer = e.target;
        
        layer.setStyle({
          weight: 3,
          color: COLORS.gold,
          fillOpacity: 0.7
        });
        
        // Count total clients in this state
        const stateData = countyStatsByState[stateAbbr] || {};
        const totalClients = Object.values(stateData).reduce((sum, county) => sum + (county.clientCount || 0), 0);
        
        if (totalClients > 0) {
          const clientLabel = selectedProgramFilter === 'ALL' 
            ? 'clients' 
            : `${selectedProgramFilter} clients`;
          
          layer.bindTooltip(`
            <div style="font-family: 'Darker Grotesque', sans-serif; padding: 0.5rem;">
              <strong style="font-size: 1.1rem; color: ${COLORS.blue};">${stateName}</strong><br/>
              <span style="color: ${COLORS.gold}; font-size: 1.2rem; font-weight: 800;">${totalClients}</span> <span style="font-size: 0.95rem;">${clientLabel}</span>
            </div>
          `, {
            permanent: false,
            direction: 'auto'
          }).openTooltip();
        }
      },
      mouseout: (e) => {
        const layer = e.target;
        layer.setStyle(stateStyle(feature));
        layer.closeTooltip();
      },
      click: () => {
        // Open state detail panel
        const stateData = countyStatsByState[stateAbbr] || {};
        const totalClients = Object.values(stateData).reduce((sum, county) => sum + (county.clientCount || 0), 0);
        
        if (totalClients > 0) {
          // Aggregate all county data for this state
          const aggregatedData = {
            state: stateAbbr,
            clientCount: totalClients,
            programs: {},
            demographics: {
              ageGroups: {},
              race: {},
              ethnicity: {},
              gender: {},
              tribalAffiliation: {}
            }
          };
          
          // Combine program data and demographics from all counties
          Object.values(stateData).forEach(countyData => {
            // Aggregate programs
            if (countyData.programs) {
              Object.entries(countyData.programs).forEach(([program, count]) => {
                aggregatedData.programs[program] = (aggregatedData.programs[program] || 0) + count;
              });
            }
            
            // Aggregate demographics
            if (countyData.demographics) {
              // Age groups
              if (countyData.demographics.ageGroups) {
                Object.entries(countyData.demographics.ageGroups).forEach(([ageGroup, count]) => {
                  aggregatedData.demographics.ageGroups[ageGroup] = 
                    (aggregatedData.demographics.ageGroups[ageGroup] || 0) + count;
                });
              }
              
              // Race
              if (countyData.demographics.race) {
                Object.entries(countyData.demographics.race).forEach(([race, count]) => {
                  aggregatedData.demographics.race[race] = 
                    (aggregatedData.demographics.race[race] || 0) + count;
                });
              }
              
              // Ethnicity
              if (countyData.demographics.ethnicity) {
                Object.entries(countyData.demographics.ethnicity).forEach(([ethnicity, count]) => {
                  aggregatedData.demographics.ethnicity[ethnicity] = 
                    (aggregatedData.demographics.ethnicity[ethnicity] || 0) + count;
                });
              }
              
              // Gender
              if (countyData.demographics.gender) {
                Object.entries(countyData.demographics.gender).forEach(([gender, count]) => {
                  aggregatedData.demographics.gender[gender] = 
                    (aggregatedData.demographics.gender[gender] || 0) + count;
                });
              }
              
              // Tribal Affiliation
              if (countyData.demographics.tribalAffiliation) {
                Object.entries(countyData.demographics.tribalAffiliation).forEach(([tribal, count]) => {
                  aggregatedData.demographics.tribalAffiliation[tribal] = 
                    (aggregatedData.demographics.tribalAffiliation[tribal] || 0) + count;
                });
              }
            }
          });
          
          setSelectedCounty({ 
            name: stateName, 
            state: stateAbbr,
            data: aggregatedData,
            isState: true // Flag to indicate this is a state, not a county
          });
        }
      }
    });
  };
  
  // Calculate map center based on states with data
  const mapCenter = useMemo(() => {
    // State center coordinates
    const stateCenters = {
      'ID': [44.0, -114.5],
      'MT': [47.0, -109.5],
      'ND': [47.5, -100.5],
      'OR': [44.0, -120.5],
      'SD': [44.5, -100.0],
      'UT': [39.3, -111.5],
      'WA': [47.5, -120.5],
      'WY': [43.0, -107.5]
    };
    
    // Find which states have data
    const activeStates = [];
    Object.keys(countyStatsByState).forEach(state => {
      if (Object.keys(countyStatsByState[state]).length > 0 && stateCenters[state]) {
        activeStates.push(stateCenters[state]);
      }
    });
    
    if (activeStates.length === 0) return [39.8, -98.6]; // Center of USA
    
    // Calculate average lat/lng of states with data
    const avgLat = activeStates.reduce((sum, [lat]) => sum + lat, 0) / activeStates.length;
    const avgLng = activeStates.reduce((sum, [, lng]) => sum + lng, 0) / activeStates.length;
    
    return [avgLat, avgLng];
  }, [countyStatsByState]);
  
  return (
    <div style={{
      background: 'white',
      borderRadius: isMobile ? '8px' : '16px',
      padding: isMobile ? '1rem' : (isTablet ? '1.5rem' : '2rem'),
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      {/* Header with title and filter */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        gap: '1rem',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <h2 style={{ 
          margin: 0, 
          color: COLORS.blue, 
          fontWeight: '800',
          fontSize: isMobile ? '1.5rem' : '2rem'
        }}>
          Client Distribution by County
        </h2>
        
        {/* Program Filter Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            padding: '0.75rem 1.5rem',
            background: selectedProgramFilter !== 'ALL' ? COLORS.gold : (showFilters ? COLORS.blue : COLORS.lightBlue),
            color: (selectedProgramFilter !== 'ALL' || showFilters) ? 'white' : COLORS.blue,
            border: `2px solid ${selectedProgramFilter !== 'ALL' ? COLORS.gold : COLORS.blue}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            whiteSpace: 'nowrap'
          }}
        >
          <span>{showFilters ? '▼' : '▶'}</span>
          {selectedProgramFilter === 'ALL' 
            ? 'Filter by Program' 
            : `Showing: ${selectedProgramFilter} (${getClientCountForProgram(selectedProgramFilter)})`}
        </button>
      </div>
      
      {/* Program Filter Dropdown */}
      <div style={{ marginBottom: '1.5rem' }}>
        {showFilters && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: COLORS.lightBlue,
            borderRadius: '8px',
            border: `2px solid ${COLORS.blue}40`
          }}>
            <div style={{ 
              marginBottom: '1rem'
            }}>
              <span style={{ fontWeight: '600', color: COLORS.blue, fontSize: '1.1rem' }}>
                Select a Program:
              </span>
              <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.25rem' }}>
                View distribution for a single program (accurate unique client counts)
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
              gap: '0.5rem',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '0.5rem'
            }}>
              {/* All Programs option */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: selectedProgramFilter === 'ALL' ? COLORS.blue : 'white',
                  color: selectedProgramFilter === 'ALL' ? 'white' : 'black',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: selectedProgramFilter === 'ALL' ? '600' : '400',
                  border: `2px solid ${selectedProgramFilter === 'ALL' ? COLORS.blue : '#ddd'}`,
                  transition: 'all 0.2s'
                }}
              >
                <input
                  type="radio"
                  name="programFilter"
                  checked={selectedProgramFilter === 'ALL'}
                  onChange={() => {
                    setSelectedProgramFilter('ALL');
                    setShowFilters(false);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <strong>All Programs</strong>
              </label>
              
              {/* Individual programs */}
              {allPrograms.map(program => (
                <label
                  key={program}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    background: selectedProgramFilter === program ? COLORS.gold : 'white',
                    color: selectedProgramFilter === program ? 'white' : 'black',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: selectedProgramFilter === program ? '600' : '400',
                    border: `2px solid ${selectedProgramFilter === program ? COLORS.gold : '#ddd'}`,
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="radio"
                    name="programFilter"
                    checked={selectedProgramFilter === program}
                    onChange={() => {
                      setSelectedProgramFilter(program);
                      setShowFilters(false);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>{program}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: '700', color: selectedProgramFilter === program ? 'white' : COLORS.gold }}>
                    ({getClientCountForProgram(program)})
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile || isTablet ? '1fr' : (selectedCounty ? '2fr 1fr' : '1fr'), 
        gap: '1.5rem',
        position: 'relative'
      }}>
        {/* Map */}
        <div 
          ref={mapContainerRef}
          style={{
            height: isMapFullscreen ? '100vh' : (isMobile ? '400px' : (isTablet ? '550px' : '750px')),
            borderRadius: isMapFullscreen ? '0' : '12px',
            overflow: 'hidden',
            border: isMapFullscreen ? 'none' : `2px solid ${COLORS.lightBlue}`,
            position: 'relative',
            background: 'white'
          }}
        >
          {/* Filter Button (only visible in fullscreen) */}
          {isMapFullscreen && (
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: selectedProgramFilter !== 'ALL' ? COLORS.gold : (showFilters ? COLORS.blue : 'white'),
                  color: (selectedProgramFilter !== 'ALL' || showFilters) ? 'white' : COLORS.blue,
                  border: `2px solid ${selectedProgramFilter !== 'ALL' ? COLORS.gold : COLORS.blue}`,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                }}
              >
                <span>{showFilters ? '▼' : '▶'}</span>
                {selectedProgramFilter === 'ALL' 
                  ? 'Filter by Program' 
                  : `Showing: ${selectedProgramFilter} (${getClientCountForProgram(selectedProgramFilter)})`}
              </button>
              
              {/* Filter Dropdown in Fullscreen */}
              {showFilters && (
                <div style={{
                  background: 'white',
                  border: `2px solid ${COLORS.blue}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  minWidth: '250px'
                }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <button
                      onClick={() => {
                        setSelectedProgramFilter('ALL');
                        setShowFilters(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: selectedProgramFilter === 'ALL' ? COLORS.blue : 'white',
                        color: selectedProgramFilter === 'ALL' ? 'white' : COLORS.blue,
                        border: `2px solid ${COLORS.blue}`,
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        fontWeight: selectedProgramFilter === 'ALL' ? '700' : '400',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s'
                      }}
                    >
                      All Programs
                    </button>
                  </div>
                  
                  <div style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: '600', 
                    color: COLORS.blue,
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Filter by Program:
                  </div>
                  
                  {uniquePrograms.map(program => (
                    <label
                      key={program}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s',
                        background: selectedProgramFilter === program ? COLORS.lightBlue : 'transparent'
                      }}
                    >
                      <input
                        type="radio"
                        name="programFilter"
                        checked={selectedProgramFilter === program}
                        onChange={() => {
                          setSelectedProgramFilter(program);
                          setShowFilters(false);
                        }}
                        style={{ cursor: 'pointer', marginRight: '0.5rem' }}
                      />
                      <span>{program}</span>
                      <span style={{ marginLeft: 'auto', fontWeight: '700', color: COLORS.gold }}>
                        ({getClientCountForProgram(program)})
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Fullscreen Toggle Button */}
          <button
            onClick={toggleMapFullscreen}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              background: 'white',
              border: `2px solid ${COLORS.blue}`,
              borderRadius: '8px',
              padding: '0.5rem',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              transition: 'all 0.2s'
            }}
            title={isMapFullscreen ? 'Exit Fullscreen' : 'Fullscreen Map'}
          >
            {isMapFullscreen ? (
              // X icon for exit
              <span style={{ fontSize: '1.5rem', color: COLORS.blue, fontWeight: 'bold' }}>✕</span>
            ) : (
              // Expand arrows icon
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.blue} strokeWidth="2.5">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            )}
          </button>
          
          <MapContainer
            center={mapCenter}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
          >
            <MapReadyHandler statesLoaded={!!geoJsonData.states} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`}
              subdomains="abcd"
              maxZoom={19}
            />
            
            {/* Regional counties (detailed view for 8 western states) - render FIRST */}
            {geoJsonData.counties && (
              <GeoJSON
                key={`counties-${selectedProgramFilter}`}
                data={geoJsonData.counties}
                pane="overlayPane"
                style={(feature) => {
                  // Extract state code from FIPS (first 2 digits)
                  const stateFips = feature.id ? feature.id.substring(0, 2) : null;
                  const fipsToState = {
                    '16': 'ID', '30': 'MT', '38': 'ND', '41': 'OR',
                    '46': 'SD', '49': 'UT', '53': 'WA', '56': 'WY'
                  };
                  const stateCode = fipsToState[stateFips] || 'OTHER';
                  return countyStyle(feature, stateCode);
                }}
                onEachFeature={(feature, layer) => {
                  const stateFips = feature.id ? feature.id.substring(0, 2) : null;
                  const fipsToState = {
                    '16': 'ID', '30': 'MT', '38': 'ND', '41': 'OR',
                    '46': 'SD', '49': 'UT', '53': 'WA', '56': 'WY'
                  };
                  const stateCode = fipsToState[stateFips] || 'OTHER';
                  onEachCounty(feature, layer, stateCode);
                }}
              />
            )}
            
            {/* US State boundaries (national overview) - render SECOND (on top, but EXCLUDE regional states) */}
            {geoJsonData.states && (() => {
              const filteredStates = geoJsonData.states.features.filter(f => {
                const stateName = f.properties.name;
                const shouldExclude = regionalStateNames.includes(stateName);
                if (shouldExclude) {
                  console.log('Excluding state:', stateName);
                }
                return !shouldExclude;
              });
              console.log('Rendering', filteredStates.length, 'non-regional states');
              
              return (
                <GeoJSON
                  key={`states-${selectedProgramFilter}`}
                  data={{
                    ...geoJsonData.states,
                    features: filteredStates
                  }}
                  pane="overlayPane"
                  style={stateStyle}
                  onEachFeature={onEachState}
                />
              );
            })()}
            
            {/* Regional state borders (outline only, rendered last so on top but non-interactive) */}
            {geoJsonData.states && (() => {
              const regionalOnly = geoJsonData.states.features.filter(f => 
                regionalStateNames.includes(f.properties.name)
              );
              
              return (
                <GeoJSON
                  key={`regional-borders-${selectedProgramFilter}`}
                  data={{
                    type: 'FeatureCollection',
                    features: regionalOnly
                  }}
                  pane="overlayPane"
                  style={() => ({
                    fill: false,
                    weight: 3,
                    opacity: 0.8,
                    color: COLORS.blue
                  })}
                  bubblingMouseEvents={false}
                />
              );
            })()}
          </MapContainer>
          
          {/* County detail panel - shows in fullscreen mode as floating panel */}
          {selectedCounty && isMapFullscreen && (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '400px',
              maxWidth: '90vw',
              maxHeight: 'calc(100vh - 20px)',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <CountyDetailPanel
                county={selectedCounty.name}
                state={selectedCounty.state}
                data={selectedCounty.data}
                onClose={() => setSelectedCounty(null)}
                isMobile={false}
                isTablet={false}
                isState={selectedCounty.isState}
              />
            </div>
          )}
        </div>
        
        {/* County detail panel - normal mode (mobile/tablet overlay, desktop side panel) */}
        {selectedCounty && !isMapFullscreen && (
          <div style={{
            position: (isMobile || isTablet) ? 'fixed' : 'relative',
            top: (isMobile || isTablet) ? 0 : 'auto',
            left: (isMobile || isTablet) ? 0 : 'auto',
            right: (isMobile || isTablet) ? 0 : 'auto',
            bottom: (isMobile || isTablet) ? 0 : 'auto',
            zIndex: (isMobile || isTablet) ? 1000 : 'auto',
            background: (isMobile || isTablet) ? 'rgba(0,0,0,0.5)' : 'transparent',
            display: 'flex',
            alignItems: (isMobile || isTablet) ? 'flex-end' : 'stretch',
            justifyContent: (isMobile || isTablet) ? 'center' : 'stretch',
          }}>
            <CountyDetailPanel
              county={selectedCounty.name}
              state={selectedCounty.state}
              data={selectedCounty.data}
              onClose={() => setSelectedCounty(null)}
              isMobile={isMobile}
              isTablet={isTablet}
              isState={selectedCounty.isState}
            />
          </div>
        )}
      </div>
      
      {/* Legend - simplified on mobile */}
      {!isMobile && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: COLORS.lightBlue,
          borderRadius: '8px',
          border: `1px solid ${COLORS.blue}40`
        }}>
          <div style={{ fontWeight: '700', marginBottom: '0.75rem', color: COLORS.blue, fontSize: isTablet ? '0.9rem' : '1rem' }}>
            Client Distribution Legend
          </div>
          <div style={{ display: 'flex', gap: isTablet ? '1rem' : '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', background: '#d3d3d3', border: '1px solid white', borderRadius: '4px' }} />
              <span style={{ fontSize: '0.85rem' }}>No Data</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', background: '#B8D4E3', border: '1px solid white', borderRadius: '4px' }} />
              <span style={{ fontSize: '0.85rem' }}>1-10</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', background: '#7BA5BF', border: '1px solid white', borderRadius: '4px' }} />
              <span style={{ fontSize: '0.85rem' }}>11-30</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', background: '#5387A4', border: '1px solid white', borderRadius: '4px' }} />
              <span style={{ fontSize: '0.85rem' }}>31-70</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', background: '#3D6B85', border: '1px solid white', borderRadius: '4px' }} />
              <span style={{ fontSize: '0.85rem' }}>71-100</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '20px', height: '20px', background: COLORS.blue, border: '1px solid white', borderRadius: '4px' }} />
              <span style={{ fontSize: '0.85rem' }}>100+</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// County Detail Panel Component
const CountyDetailPanel = ({ county, state, data, onClose, isMobile, isTablet, isState }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  const isFullScreen = isMobile || isTablet;
  
  return (
    <div style={{
      background: 'white',
      borderRadius: isFullScreen ? '16px 16px 0 0' : '12px',
      border: isFullScreen ? 'none' : `2px solid ${COLORS.blue}`,
      overflow: 'hidden',
      height: isFullScreen ? '85vh' : '600px',
      width: '100%',
      maxWidth: isFullScreen ? '100%' : 'none',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: isFullScreen ? '0 -4px 20px rgba(0,0,0,0.2)' : 'none'
    }}>
      {/* Header */}
      <div style={{
        padding: '1.5rem',
        background: COLORS.blue,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.5rem' }}>
            {county}{isState ? '' : ' County'}
          </h3>
          <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>
            {state} • {data.clientCount} clients
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0.5rem',
            borderRadius: '4px',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>
      </div>
      
      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
        {/* Demographics Section - show for both counties and states */}
        {data.demographics && (
          <div style={{
            border: `2px solid ${COLORS.blue}`,
            borderRadius: '8px',
            marginBottom: '1rem',
            overflow: 'hidden'
          }}>
            <div
              onClick={() => toggleSection('demographics')}
              style={{
                padding: '1rem',
                background: expandedSection === 'demographics' ? COLORS.lightBlue : 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <strong style={{ color: COLORS.blue, fontSize: '1.1rem' }}>📊 Demographics</strong>
              <span style={{ color: COLORS.blue }}>
                {expandedSection === 'demographics' ? '▼' : '▶'}
              </span>
            </div>
            {expandedSection === 'demographics' && (
              <div style={{ padding: '1rem', background: 'white', borderTop: `2px solid ${COLORS.blue}` }}>
                <DemographicsDisplay demographics={data.demographics} />
              </div>
            )}
          </div>
        )}
        
        {/* Program Breakdown Section */}
        <div style={{
          border: `2px solid ${COLORS.gold}`,
          borderRadius: '8px',
          marginBottom: '1rem',
          overflow: 'hidden'
        }}>
          <div
            onClick={() => toggleSection('programs')}
            style={{
              padding: '1rem',
              background: expandedSection === 'programs' ? '#FFF8E7' : 'white',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <strong style={{ color: COLORS.gold, fontSize: '1.1rem' }}>📋 Program Breakdown</strong>
            <span style={{ color: COLORS.gold }}>
              {expandedSection === 'programs' ? '▼' : '▶'}
            </span>
          </div>
          {expandedSection === 'programs' && (
            <div style={{ padding: '1rem', background: 'white', borderTop: `2px solid ${COLORS.gold}` }}>
              {data.programs && Object.keys(data.programs).length > 0 ? (
                Object.entries(data.programs)
                  .sort((a, b) => b[1] - a[1])
                  .map(([program, count]) => (
                    <div key={program} style={{
                      padding: '0.5rem 0',
                      borderBottom: '1px solid #e9ecef',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontSize: '0.95rem' }}>{program}</span>
                      <strong style={{ color: COLORS.gold }}>{count}</strong>
                    </div>
                  ))
              ) : (
                <div style={{ color: '#6c757d', fontStyle: 'italic' }}>No program data available</div>
              )}
            </div>
          )}
        </div>
        
        {/* County Stats Section - only show for counties */}
        {!isState && (
          <div style={{
            border: `2px solid ${COLORS.blue}`,
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div
              onClick={() => toggleSection('stats')}
              style={{
                padding: '1rem',
                background: expandedSection === 'stats' ? COLORS.lightBlue : 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <strong style={{ color: COLORS.blue, fontSize: '1.1rem' }}>🏛️ County Stats</strong>
              <span style={{ color: COLORS.blue }}>
                {expandedSection === 'stats' ? '▼' : '▶'}
              </span>
            </div>
            {expandedSection === 'stats' && (
              <div style={{ padding: '1rem', background: 'white', borderTop: `2px solid ${COLORS.blue}` }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Population
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: '700', color: COLORS.blue }}>
                      {data.population ? data.population.toLocaleString() : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Median Income
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: '700', color: COLORS.blue }}>
                      {data.median_income ? `$${data.median_income.toLocaleString()}` : 'N/A'}
                    </div>
                  </div>
                </div>
                
                {/* Median Age */}
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: COLORS.lightBlue,
                  borderRadius: '6px'
                }}>
                  <div style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                    Median Age
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: COLORS.blue }}>
                    {data.median_age ? `${data.median_age} years` : 'N/A'}
                  </div>
                </div>
                
                {data.population > 0 && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: COLORS.lightBlue,
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}>
                    <strong>Client-to-Population Ratio:</strong>{' '}
                    {((data.clientCount / data.population) * 100).toFixed(2)}% of county population
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// County Card Component
const CountyCard = ({ county, data }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div style={{
      border: '2px solid #dee2e6',
      borderRadius: '12px',
      marginBottom: '1rem',
      overflow: 'hidden',
      transition: 'all 0.3s',
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '1.5rem',
          background: expanded ? '#f8f9fa' : 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: COLORS.blue, fontSize: '1.5rem' }}>
            {county} County, {data.state}
          </h3>
          <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>
            <strong style={{ color: '#FF6B35' }}>{data.clientCount}</strong> clients
            {data.population > 0 && (
              <span style={{ marginLeft: '1rem' }}>
                Population: {data.population.toLocaleString()} | 
                Median Income: ${data.median_income.toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div style={{ fontSize: '1.5rem', color: COLORS.blue }}>
          {expanded ? '▼' : '▶'}
        </div>
      </div>
      
      {expanded && (
        <div style={{ padding: '1.5rem', background: 'white', borderTop: '2px solid #dee2e6' }}>
          <DemographicsDisplay demographics={data.demographics} programs={data.programs} />
        </div>
      )}
    </div>
  );
};

// Demographics Display Component
const DemographicsDisplay = ({ demographics, programs }) => {
  if (!demographics) return <div>No demographic data available</div>;
  
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
      {demographics.ageGroups && Object.keys(demographics.ageGroups).length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: COLORS.blue }}>Age Groups</h4>
          {Object.entries(demographics.ageGroups).map(([group, count]) => (
            <div key={group} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e9ecef' }}>
              <strong>{group}:</strong> {count}
            </div>
          ))}
        </div>
      )}
      
      {demographics.gender && Object.keys(demographics.gender).length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: COLORS.blue }}>Gender</h4>
          {Object.entries(demographics.gender).map(([gender, count]) => (
            <div key={gender} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e9ecef' }}>
              <strong>{gender}:</strong> {count}
            </div>
          ))}
        </div>
      )}
      
      {demographics.ethnicity && Object.keys(demographics.ethnicity).length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: COLORS.blue }}>Ethnicity</h4>
          {Object.entries(demographics.ethnicity).map(([eth, count]) => (
            <div key={eth} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e9ecef' }}>
              <strong>{eth}:</strong> {count}
            </div>
          ))}
        </div>
      )}
      
      {demographics.race && Object.keys(demographics.race).length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: COLORS.blue }}>Race</h4>
          {Object.entries(demographics.race).map(([race, count]) => (
            <div key={race} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e9ecef' }}>
              <strong>{race}:</strong> {count}
            </div>
          ))}
        </div>
      )}
      
      {demographics.tribalAffiliation && Object.keys(demographics.tribalAffiliation).length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: COLORS.blue }}>Tribal Affiliation</h4>
          {Object.entries(demographics.tribalAffiliation).map(([tribal, count]) => (
            <div key={tribal} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e9ecef' }}>
              <strong>{tribal}:</strong> {count}
            </div>
          ))}
        </div>
      )}
      
      {programs && Object.keys(programs).length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: COLORS.blue }}>Programs</h4>
          {Object.entries(programs).slice(0, 5).map(([prog, count]) => (
            <div key={prog} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e9ecef', fontSize: '0.9rem' }}>
              <strong>{prog}:</strong> {count}
            </div>
          ))}
          {Object.keys(programs).length > 5 && (
            <div style={{ padding: '0.5rem 0', fontStyle: 'italic', color: '#6c757d' }}>
              ... and {Object.keys(programs).length - 5} more programs
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Program View Component
const ProgramView = ({ data, isMobile, isTablet }) => {
  // Categorize programs
  const categories = useMemo(() => {
    const cbs = [];
    const tgh = [];
    const prtf = [];
    const staffServices = [];
    
    Object.entries(data.programData).forEach(([key, value]) => {
      if (key.startsWith('Residential_TGH_')) {
        tgh.push({ ...value, name: value.facilityName || key });
      } else if (key.startsWith('Residential_PRTF_')) {
        prtf.push({ ...value, name: value.facilityName || key });
      } else if (key === 'Staff Services') {
        staffServices.push({ ...value, name: key });
      } else {
        // All other programs are CBS
        cbs.push({ ...value, name: key });
      }
    });
    
    return { cbs, tgh, prtf, staffServices };
  }, [data.programData]);
  
  return (
    <div style={{
      background: 'white',
      borderRadius: isMobile ? '8px' : '16px',
      padding: isMobile ? '1rem' : (isTablet ? '1.5rem' : '2rem'),
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <h2 style={{ 
        margin: '0 0 1.5rem 0', 
        color: COLORS.blue,
        fontSize: isMobile ? '1.5rem' : '2rem',
        fontWeight: '800'
      }}>Program Enrollment</h2>
      
      {/* Total Unique Clients (excluding Staff Services) */}
      <div style={{
        padding: '1rem',
        background: COLORS.lightBlue,
        borderRadius: '8px',
        marginBottom: '1.5rem',
        border: `2px solid ${COLORS.blue}40`
      }}>
        <div style={{ fontSize: isMobile ? '1.1rem' : '1.3rem', fontWeight: '600', color: COLORS.blue }}>
          Total Unique Clients: <span style={{ color: COLORS.gold, fontSize: isMobile ? '1.5rem' : '2rem' }}>
            {data.categoryTotals.totalUniqueClients.toLocaleString()}
          </span>
        </div>
        <div style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.25rem' }}>
          CBS: {data.categoryTotals.cbs.toLocaleString()} | Residential: {data.categoryTotals.residential.toLocaleString()} (Staff Services: {data.categoryTotals.staffServices.toLocaleString()})
        </div>
      </div>
      
      <ProgramCategory 
        title="Community Based Services" 
        programs={categories.cbs} 
        color="#4ECDC4"
        totalUniqueClients={data.categoryTotals.cbs}
        categoryDemographics={data.categoryDemographics.cbs}
      />
      
      <ResidentialCategory 
        tghPrograms={categories.tgh}
        prtfPrograms={categories.prtf}
        totalUniqueClients={data.categoryTotals.residential}
        tghCount={data.categoryTotals.tgh}
        prtfCount={data.categoryTotals.prtf}
        categoryDemographics={data.categoryDemographics}
      />
      
      {categories.staffServices.length > 0 && (
        <ProgramCategory 
          title="Staff Services" 
          programs={categories.staffServices} 
          color="#F7B801"
          totalUniqueClients={data.categoryTotals.staffServices}
          categoryDemographics={data.categoryDemographics.staffServices}
        />
      )}
    </div>
  );
};

// Residential Category Component (with TGH and PRTF subcategories)
const ResidentialCategory = ({ tghPrograms, prtfPrograms, totalUniqueClients, tghCount, prtfCount, categoryDemographics }) => {
  const [expanded, setExpanded] = useState(false);
  const [tghExpanded, setTghExpanded] = useState(false);
  const [prtfExpanded, setPrtfExpanded] = useState(false);
  
  const totalEnrollments = useMemo(() => {
    return [...tghPrograms, ...prtfPrograms].reduce((sum, prog) => sum + (prog.clientCount || 0), 0);
  }, [tghPrograms, prtfPrograms]);
  
  return (
    <div style={{
      border: `3px solid ${COLORS.blue}`,
      borderRadius: '12px',
      marginBottom: '1.5rem',
      overflow: 'hidden',
    }}>
      {/* Main Residential Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '1.5rem',
          background: expanded ? '#f8f9fa' : 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: COLORS.blue, fontSize: '1.5rem' }}>
            Residential Services
          </h3>
          <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>
            TGH + PRTF • 
            <strong style={{ color: COLORS.blue, marginLeft: '0.5rem' }}>
              {totalUniqueClients}
            </strong> unique client{totalUniqueClients !== 1 ? 's' : ''}
            {totalEnrollments !== totalUniqueClients && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
                ({totalEnrollments} total enrollments)
              </span>
            )}
          </div>
        </div>
        <div style={{ fontSize: '1.5rem', color: COLORS.blue }}>
          {expanded ? '▼' : '▶'}
        </div>
      </div>
      
      {/* TGH and PRTF Subcategories */}
      {expanded && (
        <div style={{ padding: '1rem 1.5rem 1.5rem', background: 'white', borderTop: `2px solid ${COLORS.blue}` }}>
          {/* Overall Residential Demographics */}
          {categoryDemographics && categoryDemographics.residential && (
            <div style={{ 
              marginBottom: '2rem', 
              padding: '1.5rem', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              border: `2px solid ${COLORS.blue}`
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: COLORS.blue, fontSize: '1.2rem' }}>
                Overall Residential Demographics ({totalUniqueClients} unique clients)
              </h4>
              <DemographicsDisplay demographics={categoryDemographics.residential} />
            </div>
          )}
          
          {/* TGH Subcategory */}
          <div style={{
            border: '2px solid #95B46A',
            borderRadius: '8px',
            marginBottom: '1rem',
            overflow: 'hidden',
          }}>
            <div
              onClick={() => setTghExpanded(!tghExpanded)}
              style={{
                padding: '1rem',
                background: tghExpanded ? '#f8f9fa' : 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <h4 style={{ margin: '0 0 0.25rem 0', color: '#95B46A', fontSize: '1.3rem' }}>
                  TGH (Therapeutic Group Home)
                </h4>
                <div style={{ fontSize: '1rem', color: '#6c757d' }}>
                  {tghPrograms.length} facilit{tghPrograms.length !== 1 ? 'ies' : 'y'} • 
                  <strong style={{ color: '#95B46A', marginLeft: '0.5rem' }}>
                    {tghCount}
                  </strong> client{tghCount !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ fontSize: '1.3rem', color: '#95B46A' }}>
                {tghExpanded ? '▼' : '▶'}
              </div>
            </div>
            
            {tghExpanded && (
              <div style={{ padding: '1rem', background: 'white', borderTop: '2px solid #95B46A' }}>
                {/* TGH Demographics */}
                {categoryDemographics && categoryDemographics.tgh && (
                  <div style={{ 
                    marginBottom: '1.5rem', 
                    padding: '1rem', 
                    background: '#f8f9fa', 
                    borderRadius: '8px'
                  }}>
                    <h5 style={{ margin: '0 0 0.75rem 0', color: '#95B46A', fontSize: '1.1rem' }}>
                      TGH Demographics ({tghCount} clients)
                    </h5>
                    <DemographicsDisplay demographics={categoryDemographics.tgh} />
                  </div>
                )}
                
                <h5 style={{ margin: '0 0 0.75rem 0', color: '#6c757d', fontSize: '1rem' }}>
                  Individual Facilities:
                </h5>
                {tghPrograms.map((prog, idx) => (
                  <ProgramDetail key={idx} program={prog} color="#95B46A" />
                ))}
              </div>
            )}
          </div>
          
          {/* PRTF Subcategory */}
          <div style={{
            border: '2px solid #FF6B35',
            borderRadius: '8px',
            overflow: 'hidden',
          }}>
            <div
              onClick={() => setPrtfExpanded(!prtfExpanded)}
              style={{
                padding: '1rem',
                background: prtfExpanded ? '#f8f9fa' : 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <h4 style={{ margin: '0 0 0.25rem 0', color: '#FF6B35', fontSize: '1.3rem' }}>
                  PRTF (Psychiatric Residential Treatment Facility)
                </h4>
                <div style={{ fontSize: '1rem', color: '#6c757d' }}>
                  {prtfPrograms.length} facilit{prtfPrograms.length !== 1 ? 'ies' : 'y'} • 
                  <strong style={{ color: '#FF6B35', marginLeft: '0.5rem' }}>
                    {prtfCount}
                  </strong> client{prtfCount !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ fontSize: '1.3rem', color: '#FF6B35' }}>
                {prtfExpanded ? '▼' : '▶'}
              </div>
            </div>
            
            {prtfExpanded && (
              <div style={{ padding: '1rem', background: 'white', borderTop: '2px solid #FF6B35' }}>
                {/* PRTF Demographics */}
                {categoryDemographics && categoryDemographics.prtf && (
                  <div style={{ 
                    marginBottom: '1.5rem', 
                    padding: '1rem', 
                    background: '#f8f9fa', 
                    borderRadius: '8px'
                  }}>
                    <h5 style={{ margin: '0 0 0.75rem 0', color: '#FF6B35', fontSize: '1.1rem' }}>
                      PRTF Demographics ({prtfCount} clients)
                    </h5>
                    <DemographicsDisplay demographics={categoryDemographics.prtf} />
                  </div>
                )}
                
                <h5 style={{ margin: '0 0 0.75rem 0', color: '#6c757d', fontSize: '1rem' }}>
                  Individual Facilities:
                </h5>
                {prtfPrograms.map((prog, idx) => (
                  <ProgramDetail key={idx} program={prog} color="#FF6B35" />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Program Category Component
const ProgramCategory = ({ title, programs, color, totalUniqueClients, categoryDemographics }) => {
  const [expanded, setExpanded] = useState(false);
  
  const totalEnrollments = useMemo(() => {
    return programs.reduce((sum, prog) => sum + (prog.clientCount || 0), 0);
  }, [programs]);
  
  // If only 1 program, show its demographics directly (no nested expansion needed)
  const singleProgram = programs.length === 1;
  
  return (
    <div style={{
      border: `3px solid ${color}`,
      borderRadius: '12px',
      marginBottom: '1.5rem',
      overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '1.5rem',
          background: expanded ? '#f8f9fa' : 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: color, fontSize: '1.5rem' }}>
            {title}
          </h3>
          <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>
            {programs.length} program{programs.length !== 1 ? 's' : ''} • 
            <strong style={{ color: color, marginLeft: '0.5rem' }}>
              {totalUniqueClients}
            </strong> unique client{totalUniqueClients !== 1 ? 's' : ''}
            {totalEnrollments !== totalUniqueClients && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
                ({totalEnrollments} total enrollments)
              </span>
            )}
          </div>
        </div>
        <div style={{ fontSize: '1.5rem', color: color }}>
          {expanded ? '▼' : '▶'}
        </div>
      </div>
      
      {expanded && (
        <div style={{ padding: '1.5rem', background: 'white', borderTop: `2px solid ${color}` }}>
          {/* Show category-level demographics */}
          {categoryDemographics && (
            <div style={{ 
              marginBottom: singleProgram ? '0' : '2rem', 
              padding: '1.5rem', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              border: `2px solid ${color}`
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: color, fontSize: '1.2rem' }}>
                {singleProgram ? 'Demographics' : `Overall Demographics (${totalUniqueClients} unique clients)`}
              </h4>
              <DemographicsDisplay demographics={categoryDemographics} />
            </div>
          )}
          
          {/* Only show individual programs if there's more than 1 */}
          {!singleProgram && (
            <>
              <h4 style={{ margin: '0 0 1rem 0', color: '#6c757d', fontSize: '1.1rem' }}>
                Individual Programs:
              </h4>
              {programs.map((prog, idx) => (
                <ProgramDetail key={idx} program={prog} color={color} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Program Detail Component
const ProgramDetail = ({ program, color }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div style={{
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      marginBottom: '1rem',
      overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '1rem',
          background: expanded ? '#f8f9fa' : 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <strong style={{ color: color, fontSize: '1.2rem' }}>{program.name}</strong>
          <div style={{ color: '#6c757d', marginTop: '0.25rem' }}>
            {program.clientCount} client{program.clientCount !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ color: color }}>
          {expanded ? '▼' : '▶'}
        </div>
      </div>
      
      {expanded && (
        <div style={{ padding: '1rem', background: 'white', borderTop: '1px solid #dee2e6' }}>
          <DemographicsDisplay demographics={program.demographics} />
        </div>
      )}
    </div>
  );
};

// Beds View Component
const BedsView = ({ data, isMobile, isTablet }) => {
  if (!data || (!data.tgh && !data.prtf)) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '4rem 2rem',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      }}>
        <h2 style={{ color: COLORS.blue }}>No Bed Data Available</h2>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: isMobile ? '8px' : '16px',
      padding: isMobile ? '1rem' : (isTablet ? '1.5rem' : '2rem'),
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <h2 style={{ 
        margin: '0 0 2rem 0', 
        color: COLORS.blue,
        fontSize: isMobile ? '1.5rem' : '2rem',
        fontWeight: '800'
      }}>Residential Bed Capacity</h2>
      
      {/* Capacity Gauges */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '2rem',
        marginBottom: '3rem'
      }}>
        <CapacityGauge 
          title="PRTF Utilization" 
          data={data.prtf}
          color={COLORS.blue}
          isMobile={isMobile}
        />
        <CapacityGauge 
          title="TGH Utilization" 
          data={data.tgh}
          color={COLORS.green}
          isMobile={isMobile}
        />
      </div>
      
      {/* PRTF Facilities */}
      <FacilityList 
        title="PRTF Facilities"
        facilities={data.prtf.facilities}
        totalData={data.prtf}
        color={COLORS.blue}
        isMobile={isMobile}
        isTablet={isTablet}
      />
      
      {/* TGH Facilities */}
      <FacilityList 
        title="TGH Facilities"
        facilities={data.tgh.facilities}
        totalData={data.tgh}
        color={COLORS.green}
        isMobile={isMobile}
        isTablet={isTablet}
      />
    </div>
  );
};

// Capacity Gauge Component (Dual gauges - Operational and Licensed)
const CapacityGauge = ({ title, data, color, isMobile }) => {
  const { totalCensus, totalOperational, totalLicensed, facilities } = data;
  
  // Calculate percentages
  const operationalPct = totalOperational > 0 ? (totalCensus / totalOperational) * 100 : 0;
  const licensedPct = totalLicensed > 0 ? (totalCensus / totalLicensed) * 100 : 0;
  const bedsOffline = totalLicensed - totalOperational;
  
  const [showOfflineDetails, setShowOfflineDetails] = React.useState(false);
  
  // Get list of facilities with offline beds
  const getOfflineFacilities = () => {
    return facilities
      .filter(f => f.licensedCapacity !== f.operationalCapacity)
      .map(f => {
        const offline = f.licensedCapacity - f.operationalCapacity;
        const noteText = f.notes ? ` (${f.notes})` : '';
        return { name: f.name, offline, notes: noteText };
      });
  };
  
  const offlineFacilities = getOfflineFacilities();
  
  return (
    <div style={{
      padding: '1.5rem',
      background: `${color}10`,
      border: `2px solid ${color}40`,
      borderRadius: '12px'
    }}>
      <h3 style={{ 
        margin: '0 0 1.5rem 0',
        color,
        fontSize: isMobile ? '1.2rem' : '1.5rem',
        fontWeight: '700',
        textAlign: 'center'
      }}>{title}</h3>
      
      {/* Dual Gauges */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <SingleGauge
          label="of Operational"
          percentage={operationalPct}
          isMobile={isMobile}
        />
        <SingleGauge
          label="of Licensed"
          percentage={licensedPct}
          isMobile={isMobile}
        />
      </div>
      
      {/* Stats Grid */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1rem',
        fontSize: '0.9rem',
        marginBottom: bedsOffline > 0 ? '1rem' : '0'
      }}>
        <div>
          <div style={{ color: '#6c757d', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Census</div>
          <div style={{ fontSize: '1.4rem', fontWeight: '700', color }}>
            {totalCensus}
          </div>
        </div>
        <div>
          <div style={{ color: '#6c757d', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Operational</div>
          <div style={{ fontSize: '1.4rem', fontWeight: '700', color }}>
            {totalOperational}
          </div>
        </div>
        <div>
          <div style={{ color: '#6c757d', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Licensed</div>
          <div style={{ fontSize: '1.4rem', fontWeight: '700', color: COLORS.blue }}>
            {totalLicensed}
          </div>
        </div>
        <div>
          <div style={{ color: '#6c757d', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Available</div>
          <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#28a745' }}>
            {Math.max(0, totalOperational - totalCensus)}
          </div>
        </div>
      </div>
      
      {bedsOffline > 0 && (
        <div>
          <div 
            style={{ 
              padding: '0.75rem',
              background: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onClick={() => setShowOfflineDetails(!showOfflineDetails)}
            onMouseEnter={(e) => e.currentTarget.style.background = '#ffe69c'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff3cd'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>⚠️</span>
              <span style={{ fontWeight: '600' }}>{bedsOffline} beds offline</span>
            </div>
            <span style={{ fontSize: '1.2rem' }}>{showOfflineDetails ? '▼' : '▶'}</span>
          </div>
          
          {showOfflineDetails && (
            <div style={{
              marginTop: '0.5rem',
              padding: '0.75rem',
              background: '#fffbf0',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              fontSize: '0.9rem'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#856404' }}>Offline Beds:</div>
              {offlineFacilities.map((f, idx) => (
                <div key={idx} style={{ paddingLeft: '1rem', marginBottom: '0.25rem', color: '#6c757d' }}>
                  • {f.name} - {f.offline} bed{f.offline !== 1 ? 's' : ''}{f.notes}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Single Gauge Component using react-d3-speedometer
const SingleGauge = ({ label, percentage, isMobile }) => {
  const gaugeValue = Math.max(50, Math.min(105, percentage));
  const textColor = percentage > 100 ? '#ffc107' : percentage >= 95 ? '#28a745' : '#dc3545';
  const lowCensus = percentage < 50;

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '700', color: textColor, marginBottom: '0.25rem' }}>
        {percentage.toFixed(0)}%
      </div>
      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '0.5rem' }}>
        {label}
      </div>

      <ReactSpeedometer
        value={gaugeValue}
        minValue={50}
        maxValue={105}
        width={isMobile ? 160 : 200}
        height={isMobile ? 120 : 140}
        needleColor={COLORS.blue}
        startColor="#dc3545"
        segments={3}
        endColor="#ffc107"
        customSegmentStops={[50, 95, 100, 105]}
        segmentColors={['rgba(220, 53, 69, 0.5)', 'rgba(40, 167, 69, 0.55)', 'rgba(255, 193, 7, 0.55)']}
        currentValueText=" "
        needleHeightRatio={0.7}
        ringWidth={20}
        needleTransitionDuration={1000}
        needleTransition="easeElastic"
        textColor="#6c757d"
      />
      {lowCensus && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.25rem 0.75rem',
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          fontSize: '0.75rem',
          color: '#856404',
          fontWeight: '600'
        }}>
          ⚠ Low census — below 50%
        </div>
      )}
    </div>
  );
};

// Facility List Component
const FacilityList = ({ title, facilities, totalData, color, isMobile, isTablet }) => {
  const utilizationPct = totalData.totalOperational > 0 
    ? ((totalData.totalCensus / totalData.totalOperational) * 100).toFixed(0)
    : 0;
  
  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        background: `${color}10`,
        borderRadius: '8px'
      }}>
        <h3 style={{ margin: 0, color, fontSize: isMobile ? '1.1rem' : '1.3rem', fontWeight: '700' }}>
          {title}
        </h3>
        <div style={{ fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: '600', color: '#6c757d' }}>
          {totalData.totalCensus} / {totalData.totalOperational} beds ({utilizationPct}%)
        </div>
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)'),
        gap: '0.75rem'
      }}>
        {facilities.map(facility => (
          <FacilityRow key={facility.name} facility={facility} isMobile={isMobile} />
        ))}
      </div>
    </div>
  );
};

// Facility Row Component
const FacilityRow = ({ facility, isMobile }) => {
  const { name, census, operationalCapacity, licensedCapacity, notes } = facility;
  
  // Calculate percentage
  const percentage = operationalCapacity > 0 ? (census / operationalCapacity) * 100 : 0;
  
  // Get color
  const getColor = (pct) => {
    if (pct > 100) return '#ffc107';
    if (pct >= 95) return '#28a745';
    return '#dc3545';
  };
  
  const barColor = getColor(percentage);
  const isOffline = operationalCapacity === 0;
  const hasBedsOffline = licensedCapacity !== operationalCapacity;
  const hasException = isOffline || hasBedsOffline;
  const available = Math.max(0, operationalCapacity - census);
  
  return (
    <div 
      style={{
        padding: '1rem',
        background: 'white',
        border: `2px solid ${hasException ? '#ffc107' : '#dee2e6'}`,
        borderRadius: '8px',
        transition: 'all 0.2s'
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <strong style={{ fontSize: isMobile ? '0.95rem' : '1rem' }}>{name}</strong>
          {hasBedsOffline && <span>⚠️</span>}
          {isOffline && <span style={{ 
            padding: '0.25rem 0.5rem',
            background: '#dc3545',
            color: 'white',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>OFFLINE</span>}
        </div>
        <div style={{ 
          fontSize: isMobile ? '0.9rem' : '1rem',
          fontWeight: '600',
          color: isOffline ? '#dc3545' : barColor
        }}>
          {census}/{operationalCapacity} ({percentage.toFixed(0)}%)
          {percentage >= 95 && percentage <= 100 && ' 🟢'}
          {percentage > 100 && ' 🟡'}
          {percentage < 95 && ' 🔴'}
        </div>
      </div>
      
      {/* Progress bar - empty gray for 0/0, not filled */}
      <div style={{
        height: '12px',
        background: '#e9ecef',
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '0.75rem'
      }}>
        <div style={{
          height: '100%',
          width: operationalCapacity === 0 ? '0%' : `${Math.min(percentage, 100)}%`,
          background: isOffline ? '#6c757d' : barColor,
          transition: 'width 0.3s'
        }} />
      </div>
      
      {/* Info grid - compact 2x2 */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.5rem',
        fontSize: '0.85rem',
        color: '#6c757d',
        marginTop: '0.5rem'
      }}>
        <div style={{ 
          padding: '0.4rem 0.6rem',
          background: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <strong>Occupied:</strong> {census}
        </div>
        <div style={{ 
          padding: '0.4rem 0.6rem',
          background: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <strong>Available:</strong> {available}
        </div>
        <div style={{ 
          padding: '0.4rem 0.6rem',
          background: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <strong>Operational:</strong> {operationalCapacity}
        </div>
        <div style={{ 
          padding: '0.4rem 0.6rem',
          background: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <strong>Licensed:</strong> {licensedCapacity}
        </div>
      </div>
      
      {notes && (
        <div style={{ 
          marginTop: '0.75rem',
          fontSize: '0.95rem',
          color: '#495057',
          lineHeight: '1.5'
        }}>
          <strong>Notes:</strong> {notes}
        </div>
      )}
    </div>
  );
};

// Archive-only tabular view of counties. The map answers "where are our kids",
// this answers "give me the numbers" -- sortable, and expandable into each county's
// program mix, which is the shape people actually need for a report.
const CountyView = ({ data, isMobile, isTablet }) => {
  const [sortKey, setSortKey] = useState('clients');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState({});
  const [stateFilter, setStateFilter] = useState('ALL');

  const allRows = Object.entries(data || {}).map(([key, county]) => ({
    key,
    name: county.county || key,
    state: county.state && county.state !== 'Unknown' ? county.state : 'Unknown',
    clientCount: county.clientCount || 0,
    programs: county.programs || {},
  }));

  // County names repeat across states, so offer a state filter -- a Lincoln County in
  // MT next to one in WY reads as a duplicate otherwise. Busiest state first.
  const stateTotals = allRows.reduce((acc, r) => {
    acc[r.state] = (acc[r.state] || 0) + r.clientCount;
    return acc;
  }, {});
  const states = Object.keys(stateTotals).sort((a, b) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return stateTotals[b] - stateTotals[a];
  });

  const rows = stateFilter === 'ALL' ? allRows : allRows.filter((r) => r.state === stateFilter);

  const sorted = [...rows].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'county') return a.name.localeCompare(b.name) * dir;
    // Ties on client count read better alphabetically than arbitrarily.
    if (a.clientCount === b.clientCount) return a.name.localeCompare(b.name);
    return (a.clientCount - b.clientCount) * dir;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'county' ? 'asc' : 'desc');
    }
  };

  const toggleRow = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const totalClients = rows.reduce((sum, r) => sum + r.clientCount, 0);
  const arrow = (key) => (sortKey !== key ? '' : sortDir === 'asc' ? ' ▲' : ' ▼');

  const headerCell = {
    padding: isMobile ? '0.6rem 0.5rem' : '0.75rem 1rem',
    background: COLORS.blue,
    color: 'white',
    fontWeight: '700',
    fontSize: isMobile ? '0.9rem' : '1rem',
    cursor: 'pointer',
    userSelect: 'none',
    textAlign: 'left',
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: isMobile ? '1rem' : '1.5rem',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    }}>
      <h2 style={{ color: COLORS.blue, marginTop: 0, marginBottom: '0.25rem' }}>
        Clients by County
      </h2>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.75rem',
        margin: '0 0 1rem 0',
      }}>
        <label style={{ fontSize: '0.9rem', color: '#495057', fontWeight: '600' }}>
          State:{' '}
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            style={{
              padding: '0.35rem 0.5rem',
              borderRadius: '6px',
              border: `1px solid ${COLORS.blue}`,
              color: COLORS.blue,
              fontWeight: '600',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            <option value="ALL">All states</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {s} ({stateTotals[s].toLocaleString()})
              </option>
            ))}
          </select>
        </label>
        <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
          {rows.length.toLocaleString()} {rows.length === 1 ? 'county' : 'counties'} ·{' '}
          {totalClients.toLocaleString()} clients. Click a column heading to sort, or{' '}
          <strong>+</strong> to see a county's program mix.
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr>
              <th style={{ ...headerCell, width: '3rem', cursor: 'default', textAlign: 'center' }} aria-hidden="true"></th>
              <th style={headerCell} onClick={() => toggleSort('county')}
                  title="Sort by county name">County{arrow('county')}</th>
              <th style={{ ...headerCell, textAlign: 'right' }} onClick={() => toggleSort('clients')}
                  title="Sort by client count">Clients{arrow('clients')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const isOpen = !!expanded[row.key];
              const programs = Object.entries(row.programs)
                .filter(([, count]) => count > 0)
                .sort((a, b) => b[1] - a[1]);
              return (
                <React.Fragment key={row.key}>
                  <tr style={{ background: i % 2 ? '#f8f9fa' : 'white', borderBottom: '1px solid #e9ecef' }}>
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                      <button
                        onClick={() => toggleRow(row.key)}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${row.name}`}
                        style={{
                          width: '1.6rem', height: '1.6rem', lineHeight: 1,
                          border: `1px solid ${COLORS.blue}`, borderRadius: '4px',
                          background: isOpen ? COLORS.blue : 'white',
                          color: isOpen ? 'white' : COLORS.blue,
                          cursor: programs.length ? 'pointer' : 'not-allowed',
                          opacity: programs.length ? 1 : 0.35,
                          fontWeight: '700', fontSize: '1rem',
                        }}
                        disabled={!programs.length}
                      >
                        {isOpen ? '−' : '+'}
                      </button>
                    </td>
                    <td style={{ padding: isMobile ? '0.5rem' : '0.6rem 1rem', fontWeight: '600' }}>
                      {row.name}
                      {stateFilter === 'ALL' && row.state !== 'Unknown' ? `, ${row.state}` : ''}
                    </td>
                    <td style={{ padding: isMobile ? '0.5rem' : '0.6rem 1rem', textAlign: 'right', fontWeight: '700', color: COLORS.blue }}>
                      {row.clientCount.toLocaleString()}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr style={{ background: COLORS.lightBlue }}>
                      <td></td>
                      <td colSpan="2" style={{ padding: isMobile ? '0.5rem' : '0.75rem 1rem' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                          <tbody>
                            {programs.map(([name, count]) => (
                              <tr key={name}>
                                <td style={{ padding: '0.25rem 0', color: '#495057' }}>{name}</td>
                                <td style={{ padding: '0.25rem 0', textAlign: 'right', fontWeight: '600', color: COLORS.blue }}>
                                  {count.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.78rem', color: '#6c757d', fontStyle: 'italic' }}>
                          A client in more than one program appears under each, so these can total
                          more than the {row.clientCount.toLocaleString()} distinct clients above.
                        </p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Distinguishable in order; eight is the practical ceiling before lines stop being
// tellable apart, which is why breakdowns default to a top 8.
const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100',
                       '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

const TREND_DIMENSIONS = [
  { key: 'totals', label: 'Total only' },
  { key: 'service', label: 'Service level' },
  { key: 'programs', label: 'Program' },
  { key: 'counties', label: 'County' },
  { key: 'states', label: 'State' },
];

// Module scope keeps this stable across renders, so the memo below can depend on
// `dimension` alone instead of a function identity that changes every time.
const trendValues = (period, dimension) => {
  if (dimension === 'totals') return { 'Total unique clients': period.totals.totalUniqueClients };
  if (dimension === 'service') {
    return {
      'CBS': period.totals.cbs,
      'Residential (PRTF + TGH)': period.totals.residential,
      'PRTF': period.totals.prtf,
      'TGH': period.totals.tgh,
    };
  }
  return period[dimension] || {};
};

// Archive-only. Charts distinct clients across every period at once, which is why it
// reads the small derived trends.json rather than 19 full period payloads.
const TrendsView = ({ periodKind, isMobile, isTablet }) => {
  const [trends, setTrends] = useState(null);
  const [error, setError] = useState(null);
  const [dimension, setDimension] = useState('service');
  const [chosen, setChosen] = useState(null);      // null = auto top 8
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    fetch(`${BLOB_BASE}/archive/trends.json?t=${new Date().getTime()}`, { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load trends: ${r.status}`);
        return r.json();
      })
      .then(setTrends)
      .catch((e) => setError(e.message));
  }, []);

  // Changing dimension invalidates any hand-picked series.
  useEffect(() => { setChosen(null); setPickerOpen(false); }, [dimension]);

  const periods = useMemo(() => (trends?.periods || [])
    .filter((p) => p.kind === periodKind)
    .sort((a, b) => a.year - b.year), [trends, periodKind]);

  // Rank by total across all periods so the default 8 are the ones that actually matter.
  const ranked = useMemo(() => {
    const totals = {};
    periods.forEach((p) => {
      Object.entries(trendValues(p, dimension)).forEach(([name, n]) => {
        totals[name] = (totals[name] || 0) + (n || 0);
      });
    });
    return Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
  }, [periods, dimension]);

  const seriesNames = (chosen && chosen.length ? chosen : ranked.slice(0, 8));

  const series = seriesNames.map((name, i) => ({
    name,
    color: SERIES_COLORS[i % SERIES_COLORS.length],
    points: periods.map((p) => (trendValues(p, dimension)[name] || 0)),
  }));

  if (error) {
    return (
      <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ color: '#D9534F' }}>Trends unavailable</h2>
        <p style={{ color: '#6c757d' }}>{error}</p>
      </div>
    );
  }
  if (!trends) {
    return (
      <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
        Loading trends…
      </div>
    );
  }

  const W = 1000, H = 420;
  const M = { top: 20, right: 20, bottom: 54, left: 56 };
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;

  const maxVal = Math.max(1, ...series.flatMap((s) => s.points));
  const step = Math.max(1, Math.ceil(maxVal / 5 / 50) * 50);
  const yMax = Math.ceil(maxVal / step) * step;
  const ticks = [];
  for (let v = 0; v <= yMax; v += step) ticks.push(v);

  const x = (i) => (periods.length <= 1
    ? M.left + plotW / 2
    : M.left + (i / (periods.length - 1)) * plotW);
  const y = (v) => M.top + plotH - (v / yMax) * plotH;

  const controlStyle = {
    padding: '0.35rem 0.5rem',
    borderRadius: '6px',
    border: `1px solid ${COLORS.blue}`,
    color: COLORS.blue,
    fontWeight: '600',
    fontSize: '0.9rem',
    cursor: 'pointer',
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: isMobile ? '1rem' : '1.5rem',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    }}>
      <h2 style={{ color: COLORS.blue, marginTop: 0, marginBottom: '0.25rem' }}>
        Unique Clients Over Time
      </h2>
      <p style={{ color: '#6c757d', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
        Distinct clients served in each {periodKind === 'fiscal' ? 'fiscal' : 'calendar'} year.
        A client counts once per period no matter how many programs they touched, so series can
        overlap without inflating any single total.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <label style={{ fontSize: '0.9rem', color: '#495057', fontWeight: '600' }}>
          Break down by:{' '}
          <select value={dimension} onChange={(e) => setDimension(e.target.value)} style={controlStyle}>
            {TREND_DIMENSIONS.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
        </label>

        {ranked.length > 8 && (
          <button onClick={() => setPickerOpen(!pickerOpen)} style={{ ...controlStyle, background: 'white' }}>
            {pickerOpen ? 'Hide' : 'Choose'} series ({seriesNames.length} of {ranked.length})
          </button>
        )}
        {chosen && chosen.length > 0 && (
          <button onClick={() => setChosen(null)} style={{ ...controlStyle, background: 'white' }}>
            Reset to top 8
          </button>
        )}
      </div>

      {pickerOpen && (
        <div style={{
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '0.75rem',
          marginBottom: '1rem',
          maxHeight: '200px',
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '0.25rem 1rem',
        }}>
          {ranked.map((name) => {
            const on = seriesNames.includes(name);
            return (
              <label key={name} style={{ fontSize: '0.85rem', color: '#495057', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const base = chosen && chosen.length ? chosen : ranked.slice(0, 8);
                    setChosen(on ? base.filter((n) => n !== name) : [...base, name]);
                  }}
                  style={{ marginRight: '0.4rem' }}
                />
                {name}
              </label>
            );
          })}
        </div>
      )}

      {periods.length === 0 ? (
        <p style={{ color: '#6c757d' }}>No {periodKind} periods have been compiled yet.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: isMobile ? '560px' : 'auto', height: 'auto' }}
                 role="img" aria-label="Unique clients per period">
              {ticks.map((v) => (
                <g key={v}>
                  <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} stroke="#e9ecef" strokeWidth="1" />
                  <text x={M.left - 8} y={y(v) + 4} textAnchor="end" fill="#898781" fontSize="11">
                    {v.toLocaleString()}
                  </text>
                </g>
              ))}
              <line x1={M.left} x2={W - M.right} y1={y(0)} y2={y(0)} stroke="#c3c2b7" strokeWidth="1" />

              {periods.map((p, i) => (
                <text key={p.id} x={x(i)} y={y(0) + 20} textAnchor="middle" fill="#898781" fontSize="11">
                  {p.year}
                </text>
              ))}
              <text x={M.left + plotW / 2} y={H - 8} textAnchor="middle" fill="#898781" fontSize="11">
                {periodKind === 'fiscal' ? 'Fiscal year (ending June 30)' : 'Calendar year'}
              </text>

              {hover !== null && (
                <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={y(0)}
                      stroke="#898781" strokeWidth="1" strokeDasharray="3 3" />
              )}

              {series.map((s) => (
                <g key={s.name}>
                  <path
                    d={s.points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ')}
                    fill="none" stroke={s.color} strokeWidth="2.25"
                  />
                  {s.points.map((v, i) => (
                    <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 5 : 3}
                            fill={s.color} stroke="white" strokeWidth="1.5" />
                  ))}
                </g>
              ))}

              {periods.map((p, i) => (
                <rect key={p.id} x={x(i) - plotW / Math.max(periods.length, 2) / 2} y={M.top}
                      width={plotW / Math.max(periods.length, 2)} height={plotH}
                      fill="transparent"
                      onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
              ))}
            </svg>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', marginTop: '0.75rem' }}>
            {series.map((s) => (
              <span key={s.name} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#495057' }}>
                <span style={{ width: '14px', height: '3px', borderRadius: '2px', background: s.color }} />
                {s.name}
                {hover !== null && (
                  <strong style={{ color: COLORS.blue, fontVariantNumeric: 'tabular-nums' }}>
                    {(s.points[hover] || 0).toLocaleString()}
                  </strong>
                )}
              </span>
            ))}
          </div>

          {hover !== null && (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#6c757d' }}>
              Showing {periods[hover].label}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default YBGRDashboard;